import { useCallback, useEffect, useRef, useState } from "react";
import { listUnsubscribes, type Unsubscribe } from "../api/unsubscribes";
import Icon from "../components/icons";

function fmtDate(s: string): string {
  try {
    return new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return s;
  }
}

/** Listeden Çıkanlar — tanıtım e-postasından çıkma talepleri (ETK 6563 opt-out).
 *
 *  Neden ayrı sayfa: bu tablo müşteri hesabından BAĞIMSIZ. Listedeki
 *  adreslerin çoğunun sistemde kullanıcısı yok (EUDAMED'den gelen soğuk
 *  liste), o yüzden Müşteriler ekranında görünmezler. Çıkmış bir adrese
 *  tekrar göndermek ihlal — gönderim öncesi ayıklama listesi burasıdır.
 *
 *  Mail Takibi ile de karıştırma: orası kim açtı/tıkladı ölçümü, burası
 *  "bir daha gönderme" kaydı. Ölçüm temizliği (truncate_mail_tracking.sql)
 *  bu tabloya DOKUNMAZ — dokunsaydı çıkma talepleri silinirdi. */
export default function Unsubscribes() {
  const [rows, setRows] = useState<Unsubscribe[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [kopyalandi, setKopyalandi] = useState(false);
  const aramaRef = useRef<HTMLInputElement>(null);

  const yukle = useCallback(async (arama: string) => {
    setLoading(true);
    setErr(null);
    try {
      setRows(await listUnsubscribes({ q: arama || undefined, limit: 2000 }));
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Yükleme hatası");
    } finally {
      setLoading(false);
    }
  }, []);

  // Arama yazarken her tuşta istek atmasın (ui-admin §4 — tarama işi).
  useEffect(() => {
    const t = setTimeout(() => void yukle(q.trim()), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q, yukle]);

  // "/" ile arama alanına odaklan (ui-admin §7).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const hedef = e.target as HTMLElement | null;
      if (e.key !== "/" || hedef?.tagName === "INPUT" || hedef?.tagName === "TEXTAREA") return;
      e.preventDefault();
      aramaRef.current?.focus();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Gönderim betiği listeyi API'den alıyor; bu buton elle ayıklama (Excopan'a
  // liste iletme) için — panelde görüp panoya almak tek adım.
  async function adresleriKopyala() {
    try {
      await navigator.clipboard.writeText(rows.map((r) => r.email).join("\n"));
      setKopyalandi(true);
      setTimeout(() => setKopyalandi(false), 2000);
    } catch {
      setErr("Panoya kopyalanamadı — tarayıcı izin vermedi.");
    }
  }

  return (
    <>
      <div className="toolbar">
        <input
          ref={aramaRef}
          type="search"
          placeholder="Adreste ara…  ( / )"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Adreste ara"
        />
        <button className="btn-primary" onClick={() => void yukle(q.trim())} disabled={loading}>
          Yenile
        </button>
        <button onClick={() => void adresleriKopyala()} disabled={loading || rows.length === 0}>
          {kopyalandi ? "Kopyalandı" : "Adresleri kopyala"}
        </button>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="data-table-card">
        <div className="data-table-toolbar">
          <span className="data-count" style={{ marginInlineStart: 0 }}>
            {rows.length} adres{q ? " (aramada)" : ""}
          </span>
        </div>

        {loading ? (
          <div className="empty-state"><div className="spinner-sm" /><p>Yükleniyor…</p></div>
        ) : rows.length === 0 ? (
          // Boş durum "kayıt yok" demekle yetinmez: neden boş ve bu iyi mi
          // kötü mü söyler (ui-admin §4).
          <div className="empty-state">
            <Icon name="inbox" size={34} />
            <p>
              {q
                ? "Bu aramaya uyan adres yok."
                : "Henüz kimse listeden çıkmadı. Mailin alt bilgisindeki bağlantı kullanıldıkça burası dolar."}
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="num">ID</th>
                  <th>E-posta</th>
                  <th>Kaynak</th>
                  <th className="nowrap">Çıkma zamanı</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="num">{r.id}</td>
                    <td className="mono">{r.email}</td>
                    <td>
                      <span
                        className={"badge " + (r.source === "web" ? "neutral" : "ok")}
                        title={
                          r.source === "web"
                            ? "Sitedeki listeden çıkma formundan"
                            : `Kaynak: ${r.source}`
                        }
                      >
                        {r.source === "web" ? "Site formu" : r.source}
                      </span>
                    </td>
                    <td className="nowrap">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
        Bu adreslere tanıtım maili <strong>gönderilmez</strong>. Gönderim betiği listeyi
        <code> GET /api/v1/admin/mail/unsubscribes</code> ile alıp ayıklar; buradaki tablo
        aynı kaydın okunabilir hâlidir. Kayıtlar silinmez — ölçüm temizliği bu tabloya
        dokunmaz.
      </p>
    </>
  );
}
